import { useState, useEffect } from "react";
import type React from "react";
import {
  useQuery,
  getTradovateConnections,
  disconnectTradovate,
  reconnectTradovate,
} from "wasp/client/operations";
import { RefreshCw, Wifi, Radio, AlertCircle, Loader2, Unplug, Clock, PauseCircle, MoreHorizontal } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import { Badge } from "../../client/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import useTranslation from "../../client/hooks/useTranslation";
import { cn } from "../../client/utils";
import { useToast } from "../../client/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../client/components/ui/dropdown-menu";
import { resolveSyncActions } from "./tradovateSyncActions";
import { resolveSyncPresentation } from "./tradovateSyncPresentation";
import type { SyncIcon, SyncTone } from "./tradovateSyncPresentation";

interface Props {
  connectionId?: string;
  /**
   * Quelle partie rendre. Par défaut tout (badge + actions empilés), ce qui
   * reste le comportement historique. Les mini-cards de prop firm rendent le
   * badge à gauche (près du nom) et les actions à droite, sur une seule ligne :
   * elles montent donc les deux parties séparément plutôt que le bloc empilé.
   */
  part?: "all" | "badge" | "actions";
  /** Masque la ligne de fraîcheur (« Trades reçus à l'instant », « Dernière synchro »). Défaut : affichée. */
  showFreshness?: boolean;
}

export default function TradovateSyncStatus({ connectionId, part = "all", showFreshness = true }: Props) {
  const t = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const { data: connections, refetch } = useQuery(getTradovateConnections, {});
  const connection = (connections ?? []).find((c: any) => c.id === connectionId) ?? null;

  const status = (connection as any)?.status as string | undefined;
  // Server-computed fine-grained status; falls back to the raw status so the
  // component still renders sensibly if the query shape ever lags behind.
  const displayStatus = ((connection as any)?.displayStatus as string | undefined) ?? status;
  // "not_enabled" par defaut : un client qui recoit une reponse sans ce champ
  // (version anterieure du serveur) n'affiche rien plutot que de deviner.
  const realtimeStatus =
    ((connection as any)?.realtimeStatus as string | undefined) ?? "not_enabled";
  // AVANCEMENT DE LA SYNC. Le serveur tranche (cf. tradovateSyncProgress.ts) ;
  // le client n'a qu'à afficher. "idle" par défaut : un client servi par une
  // version antérieure du serveur n'affiche rien plutôt que de deviner.
  const syncProgress =
    ((connection as any)?.syncProgress as
      | { state: string; totalsArePartial: boolean }
      | undefined) ?? { state: "idle", totalsArePartial: false };
  const syncRunning = syncProgress.state === "initial_running";
  const syncThrottled = syncProgress.state === "throttled";
  const id = connectionId ?? (connection as any)?.id;
  const lastSyncAt = (connection as any)?.lastSyncAt as Date | null | undefined;
  const lastSyncError = (connection as any)?.lastSyncError as string | null | undefined;

  // Rafraîchissement pendant la sync initiale.
  //
  // ⚠️ CE BLOC NE S'EST JAMAIS DÉCLENCHÉ. Il testait `status !== "syncing"`,
  // or aucun code n'écrit jamais `status: "syncing"` sur une
  // `TradovateConnection` (le pipeline écrit "active", "error" ou
  // "needs_reauth"). L'UI attendait donc un état que le serveur ne produisait
  // pas : ni spinner, ni rafraîchissement, pendant tout l'import initial.
  // On le branche sur le signal réel.
  useEffect(() => {
    if (!syncRunning) return;
    const interval = setInterval(() => refetch(), 5000);
    return () => clearInterval(interval);
  }, [syncRunning, refetch]);

  if (!connection || !id) return null;

  const handleReconnect = async () => {
    setLoading(true);
    try {
      const result = await reconnectTradovate({ connectionId: id });
      window.location.href = result.url;
    } catch (err: any) {
      toast({ title: t.tradovate.errorConnectionFailed, description: err?.message || undefined, variant: "destructive" });
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t.tradovate.disconnectConfirm)) return;
    setLoading(true);
    try {
      await disconnectTradovate({ connectionId: id });
      refetch();
    } catch (err: any) {
      toast({ title: t.tradovate.errorGeneric, description: err?.message || undefined, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // ── APPARENCE DU BADGE UNIQUE ────────────────────────────────────────────
  //
  // La REGLE vit dans `tradovateSyncPresentation.ts` (module pur, teste sans
  // React) ; ce composant ne fait que traduire son verdict en tokens de theme.
  // Aucun hex en dur : les 4 themes (dark, light, jungle, sunrise) doivent
  // rester lisibles.
  const toneClass: Record<SyncTone, string> = {
    positive: "text-primary border-primary/30 bg-primary/10",
    warning:
      "text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10",
    critical: "text-destructive border-destructive/30 bg-destructive/10",
    neutral: "text-muted-foreground border-border bg-muted/40",
  };

  const toneIcon: Record<SyncIcon, React.ReactNode> = {
    radio: <Radio className="h-3 w-3" />,
    wifi: <Wifi className="h-3 w-3" />,
    clock: <Clock className="h-3 w-3" />,
    pause: <PauseCircle className="h-3 w-3" />,
    alert: <AlertCircle className="h-3 w-3" />,
  };

  const s = displayStatus ?? "active";

  // UN SEUL BADGE. Le serveur produit deux axes (sync + temps reel) et c'est
  // juste ; les rendre en deux badges de meme poids ne l'etait pas. Cf.
  // l'en-tete de `tradovateSyncPresentation.ts`.
  const presentation = resolveSyncPresentation({ displayStatus: s, realtimeStatus });
  const label = (t.tradovate as Record<string, string>)[presentation.labelKey] ?? s;
  const help = presentation.helpKey
    ? (t.tradovate as Record<string, string>)[presentation.helpKey]
    : undefined;

  // Barre d'actions : une interruption DURABLE du temps reel ne laisse que la
  // reconnexion. Regle isolee dans un module pur (cf. tradovateSyncActions.ts).
  const actions = resolveSyncActions({ displayStatus: s, realtimeStatus });

  return (
    <div className={cn(part === "all" ? "flex flex-col gap-2" : "contents")}>
      {part !== "actions" && (
      // ── ALIGNEMENT INTERNE DE LA COLONNE STATUT ──────────────────────────
      // Monte seul dans une mini-card (`part="badge"`), ce groupe porte deux
      // informations de largeurs tres variables : le badge, puis la date de
      // derniere synchro. En flex libre, la date demarrait juste apres le
      // badge, donc a une abscisse differente selon le libelle -- le defaut
      // que la grille de la ligne corrige au niveau superieur, rejoue ici.
      //
      // `min-w` sur le badge (et non `w` fixe) : la colonne est stable, mais
      // un libelle plus long qu'attendu (traduction EN, futur statut)
      // s'etend au lieu d'etre tronque.
      <div
        className={cn(
          "items-center gap-2",
          // ── DEUX PISTES HERITEES, PLUTOT QU'UNE LARGEUR DEVINEE ──────────
          // Ce groupe porte le badge puis la date. Avec un simple flex, la
          // date demarrait apres un badge de largeur variable (mesure : 152,
          // 153 et 173 px selon le libelle) et se decalait d'une ligne a
          // l'autre. Un `min-w` fixe ne reglait rien : il faudrait deviner le
          // libelle le plus long, qui change avec la langue.
          //
          // En `subgrid`, les deux pistes sont celles de la grille parente :
          // elles se dimensionnent sur le contenu le plus large REELLEMENT
          // rendu, FR comme EN, sans constante a maintenir.
          part === "badge"
            ? "grid grid-cols-[auto_auto] sm:col-span-2 sm:grid sm:grid-cols-subgrid"
            : "flex flex-wrap",
        )}
      >
        {/* LE BADGE UNIQUE — verbe (la synchro) et adverbe (la vitesse) dans
            un seul libelle. « Synchronise » + « Temps reel » etaient
            redondants ; « Synchronise » + « Connexion interrompue »
            paraissaient se contredire. Une seule phrase regle les deux. */}
        <Badge
          variant="outline"
          title={help}
          className={cn(
            // `whitespace-nowrap shrink-0` PORTES PAR LE BADGE LUI-MEME : un
            // libelle long (« Sync active, aucun compte actif ») se repliait
            // sur 4 lignes des que sa colonne retrecissait, et la pastille
            // `rounded-full` devenait un rond. Le correctif precedent vivait
            // dans un selecteur du PARENT (pied de la BeamCard) : les autres
            // montages (vue tableau, fiche compte) n'en profitaient pas.
            "flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-medium",
            toneClass[presentation.tone],
            // Le badge ne porte AUCUNE largeur : c'est la piste de grille qui
            // la lui donne, calee sur le libelle le plus large rendu.
            // `w-fit` l'empeche de s'etirer sur toute la piste.
            part === "badge" && "w-fit",
          )}
        >
          {toneIcon[presentation.icon]}
          {label}
        </Badge>

        {/* SYNC INITIALE EN COURS — le signal qui manquait. Les trades sont
            écrits un par un, hors transaction : pendant l'import, les totaux
            affichés ailleurs dans l'app sont PARTIELS. Sans ce libellé, un
            utilisateur a conclu qu'il manquait des comptes, a relancé un
            import, puis a écrit au support. */}
        {syncRunning && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground animate-pulse">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t.tradovate.syncRunningTitle}
          </span>
        )}

        {/* CYCLE BRIDÉ PAR LE QUOTA — orthogonal au badge de statut : il dit
            « des comptes ont pu être sautés sur CE cycle », pas « la synchro
            est en panne ». Le statut reste donc vert à dessein. */}
        {syncThrottled && (
          <Badge
            variant="outline"
            title={t.tradovate.syncThrottledHint}
            className="flex items-center gap-1 text-xs font-medium text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10"
          >
            <Clock className="h-3 w-3" />
            {t.tradovate.syncThrottledTitle}
          </Badge>
        )}

        {/* LIGNE DE FRAICHEUR — volontairement COURTE : on ne donne pas
            l'intervalle de synchro. Annoncer « mise a jour toutes les 15 min »
            serait une promesse de cadence que l'etape 3A ne tient deja plus
            (cycle espace a 1 h sur les connexions en `ws_only`).

            ⚠️ EN DIRECT, ON N'AFFICHE PAS `lastSyncAt` : cette date est celle
            du dernier cycle REST, pas du dernier trade recu par le socket.
            Elle sous-estimerait gravement la fraicheur reelle -- jusqu'a une
            heure d'ecart -- sur precisement les connexions les plus fraiches. */}
        {showFreshness && presentation.showFreshness && presentation.isLive && (
          <span className="text-xs text-muted-foreground">{t.tradovate.lastSyncLive}</span>
        )}

        {showFreshness && presentation.showFreshness && !presentation.isLive && lastSyncAt && (
          <span className="text-xs text-muted-foreground">
            {t.tradovate.lastSync}:{" "}
            {formatDistanceToNow(new Date(lastSyncAt), { addSuffix: true })}
          </span>
        )}

        {s === "error" && lastSyncError && (
          // Never surface the raw (technical/Prisma) error to users — show a
          // generic, actionable message; keep the raw text in the tooltip only.
          <span className="text-xs text-destructive truncate max-w-[240px]" title={lastSyncError}>
            {t.tradovate.syncErrorGeneric}
          </span>
        )}
      </div>
      )}

      {part !== "badge" && (
      <div className="flex items-center gap-2 flex-wrap">
        {/* PLUS AUCUNE SYNCHRO MANUELLE. « Synchroniser maintenant » puis
            « Réimporter les trades récents » ont été retirés : ils invitaient à
            cliquer en boucle sans rien accélérer, et le réimport brûlait le
            poste le plus lourd du quota Tradovate (600 req/h par IP PARTAGÉE),
            dégradant donc la synchro de tous les utilisateurs. La collecte est
            entièrement pilotée côté serveur (cron 15 min + socket). Seule
            « Reconnecter » subsiste : re-autoriser l'OAuth exige l'utilisateur.
            Les actions serveur restent en place (cf. tradovateSyncActions.ts). */}

        {actions.reconnect && (
          <Button
            size="sm"
            variant="ghost"
            className={cn(
              "h-6 px-2 text-xs",
              // idle is a neutral lifecycle state — no warning tint (spec §2.3).
              s === "needs_reauth" || s === "foreign_token"
                ? "text-[hsl(var(--warning))]"
                : "text-muted-foreground",
            )}
            onClick={handleReconnect}
            disabled={loading}
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            {t.tradovate.reconnect}
          </Button>
        )}

        {actions.disconnect && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs text-muted-foreground"
            onClick={handleDisconnect}
            disabled={loading}
          >
            <Unplug className="h-3 w-3 mr-1" />
            {t.tradovate.disconnect}
          </Button>
        )}

        {/* DEBORDEMENT — « Deconnecter » quitte la barre quand le temps reel
            est interrompu, mais ne disparait jamais : sans ce repli, une
            connexion durablement coupee ne pourrait plus etre supprimee. */}
        {actions.overflowDisconnect && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-muted-foreground"
                disabled={loading}
                title={t.tradovate.moreActions}
                aria-label={t.tradovate.moreActions}
              >
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDisconnect} disabled={loading}>
                <Unplug className="h-3 w-3 mr-2" />
                {t.tradovate.disconnect}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      )}
    </div>
  );
}
